import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  InsertDriveFile,
  Refresh,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import axios from "../../api/axiosConfig";
import Loader from "../../components/Loader/Loader";

const KYCApproval = () => {
  const [loading, setLoading] = useState(false);

  const [kycs, setKycs] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedKyc, setSelectedKyc] = useState(null);
  const [kycModalOpen, setKycModalOpen] = useState(false);

  const [identityData, setIdentityData] = useState({
    bvn: null,
    nin: null,
  });

  const [identityLoading, setIdentityLoading] = useState({
    bvn: false,
    nin: false,
  });

  const [identityErrors, setIdentityErrors] = useState({
    bvn: null,
    nin: null,
  });

  const [showSensitive, setShowSensitive] = useState(false);
  const [approving, setApproving] = useState(false);

  const getErrorMessage = (error) =>
    error.response?.data?.message || error.message || "Something went wrong";

  const fetchPendingKycs = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.post("/marketplace/fetch-pending-kycs/", {
        params: {
          page,
          limit,
        },
      });

      setKycs(response.data?.data || []);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching pending KYCs:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchPendingKycs();
  }, [fetchPendingKycs]);

  const getKyc = (item) => item?.profile_profile?.mp_kyc || {};

  const getFullName = (item) => {
    const kyc = getKyc(item);

    return [kyc.f_name, kyc.l_name].filter(Boolean).join(" ") || "—";
  };

  const getFullNameWithMiddleName = (item) => {
    const kyc = getKyc(item);

    return (
      [kyc.f_name, kyc.o_name, kyc.l_name].filter(Boolean).join(" ") || "—"
    );
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString();
  };

  const formatDateOnly = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString();
  };

  const maskValue = (value, visibleCharacters = 4) => {
    if (!value) return "—";

    const stringValue = String(value);

    if (showSensitive) return stringValue;

    if (stringValue.length <= visibleCharacters) {
      return "••••";
    }

    return (
      "•".repeat(Math.max(4, stringValue.length - visibleCharacters)) +
      stringValue.slice(-visibleCharacters)
    );
  };

  const fetchIdentityDocument = async (type, kycId) => {
    if (!kycId) {
      setIdentityErrors((current) => ({
        ...current,
        [type]: "KYC ID was not provided.",
      }));
      return;
    }

    try {
      setIdentityLoading((current) => ({
        ...current,
        [type]: true,
      }));

      setIdentityErrors((current) => ({
        ...current,
        [type]: null,
      }));

      const response = await axios.get(
        `/marketplace/fetch-kyc-identity/${kycId}/${type}`,
      );

      const entity = response.data?.data?.entity;

      if (!entity) {
        throw new Error(`No ${type.toUpperCase()} verification data was found`);
      }

      setIdentityData((current) => ({
        ...current,
        [type]: entity,
      }));
    } catch (error) {
      console.error(
        `Error loading ${type.toUpperCase()} verification document:`,
        error,
      );

      setIdentityData((current) => ({
        ...current,
        [type]: null,
      }));

      setIdentityErrors((current) => ({
        ...current,
        [type]:
          error.response?.data?.message ||
          `Unable to load ${type.toUpperCase()} verification data.`,
      }));
    } finally {
      setIdentityLoading((current) => ({
        ...current,
        [type]: false,
      }));
    }
  };

  const openKyc = async (item) => {
    setSelectedKyc(item);
    setShowSensitive(false);
    setKycModalOpen(true);

    setIdentityData({
      bvn: null,
      nin: null,
    });

    setIdentityErrors({
      bvn: null,
      nin: null,
    });

    await Promise.all([
      fetchIdentityDocument("bvn", item.id),
      fetchIdentityDocument("nin", item.id),
    ]);
  };

  const closeKyc = () => {
    setKycModalOpen(false);
    setSelectedKyc(null);
    setShowSensitive(false);

    setIdentityData({
      bvn: null,
      nin: null,
    });

    setIdentityErrors({
      bvn: null,
      nin: null,
    });
  };

  const handleApprove = async () => {
    if (!selectedKyc?.id) return;

    const confirmed = window.confirm(
      `Approve the marketplace account for ${getFullName(selectedKyc)}?`,
    );

    if (!confirmed) return;

    try {
      setApproving(true);

      await axios.patch(
        `/marketplace/approve-pending-account/${selectedKyc.id}`,
      );

      toast.success("Marketplace account approved successfully");

      closeKyc();

      await fetchPendingKycs();
    } catch (error) {
      console.error("Error approving KYC:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setApproving(false);
    }
  };

  const renderImage = (src, alt, height = 220) => {
    if (!src) {
      return (
        <Box
          sx={{
            height,
            border: "1px dashed #ccc",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No image available</Typography>
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        onClick={(e) => {
          e.stopPropagation();
          window.open(src, "_blank", "noopener,noreferrer");
        }}
        sx={{
          width: "100%",
          height,
          objectFit: "contain",
          border: "1px solid #ddd",
          borderRadius: 2,
          backgroundColor: "#f8f8f8",
          cursor: "pointer",
        }}
      />
    );
  };

  const renderField = (label, value, sensitive = false) => (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          wordBreak: "break-word",
        }}
      >
        {sensitive ? maskValue(value) : value || "—"}
      </Typography>
    </Box>
  );

  const renderIdentityCard = (type) => {
    const isBvn = type === "bvn";
    const title = isBvn ? "BVN Verification" : "NIN Verification";

    const data = identityData[type];
    const loadingDocument = identityLoading[type];
    const error = identityErrors[type];

    return (
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>

            {data && (
              <Chip
                label="Loaded"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Stack>

          {loadingDocument ? (
            <Box
              sx={{
                minHeight: 250,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={28} />

                <Typography variant="body2" color="text.secondary">
                  Loading verification data...
                </Typography>
              </Stack>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : data ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {renderField("First Name", data.first_name)}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {renderField("Last Name", data.last_name)}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {renderField("Middle Name", data.middle_name)}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {renderField("Gender", data.gender)}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {renderField("Date of Birth", data.date_of_birth)}
                </Grid>

                <Grid item xs={12} sm={6}>
                  {renderField(
                    "Phone Number",
                    data.phone_number || data.phone_number1,
                  )}
                </Grid>

                <Grid item xs={12}>
                  {renderField(
                    isBvn ? "BVN" : "NIN",
                    isBvn ? data.bvn : data.nin,
                    true,
                  )}
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Verification Photo
                </Typography>

                {renderImage(
                  isBvn ? data.image : data.photo,
                  `${title} verification photo`,
                  240,
                )}
              </Box>
            </>
          ) : (
            <Alert severity="info">
              Verification data has not been loaded.
            </Alert>
          )}
        </Stack>
      </Paper>
    );
  };
  const selectedProfile = selectedKyc?.profile_profile;
  const selectedKycData = selectedProfile?.mp_kyc;

  return (
    <div>
      {loading && (
        <div className="overlay">
          <Loader />
        </div>
      )}

      <Box sx={{ mt: 2 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                KYC Approvals
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Review and approve pending marketplace accounts.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchPendingKycs}
              disabled={loading}
              sx={{
                color: "black",
                borderColor: "black",
                "&:hover": {
                  borderColor: "black",
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Refresh
            </Button>
          </Stack>

          <Alert severity="warning" sx={{ mb: 3 }}>
            This screen contains confidential identity information. Only use it
            for authorised KYC verification.
          </Alert>

          {kycs.length === 0 ? (
            <Alert severity="info">
              There are currently no pending KYC approvals.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Date of Birth</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {kycs.map((item) => {
                      const kyc = getKyc(item);

                      return (
                        <TableRow
                          hover
                          key={item.id}
                          onClick={() => openKyc(item)}
                          sx={{
                            cursor: "pointer",
                          }}
                        >
                          <TableCell>
                            <Typography fontWeight={700}>
                              {getFullName(item)}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              KYC ID: {item.id}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {item.profile_profile?.email || "—"}
                          </TableCell>

                          <TableCell>{kyc.phone || "—"}</TableCell>

                          <TableCell>{formatDateOnly(kyc.dob)}</TableCell>

                          <TableCell>
                            {[kyc.city_name, kyc.state_name]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label="Pending"
                              color="warning"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ mt: 3 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Page {page} of {totalPages}
                </Typography>

                <Pagination
                  page={page}
                  count={totalPages}
                  onChange={(event, value) => setPage(value)}
                  disabled={loading}
                />
              </Stack>
            </>
          )}
        </Paper>
      </Box>

      {/* KYC details */}
      <Dialog
        open={kycModalOpen}
        onClose={closeKyc}
        fullWidth
        maxWidth="xl"
        scroll="paper"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                KYC Review
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {getFullNameWithMiddleName(selectedKyc)}
              </Typography>
            </Box>

            <IconButton onClick={closeKyc}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedKyc && selectedKycData && (
            <Stack spacing={4}>
              <Alert severity="warning">
                This screen contains sensitive personal and identity
                information. Use it only for authorised KYC verification.
              </Alert>

              {/* Personal Information */}
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  Personal Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    {renderField(
                      "Full Name",
                      getFullNameWithMiddleName(selectedKyc),
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField("Email", selectedProfile?.email)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField("Phone", selectedKycData.phone)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField("Date of Birth", selectedKycData.dob)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField(
                      "Gender",
                      selectedKycData.gender === "m"
                        ? "Male"
                        : selectedKycData.gender === "f"
                          ? "Female"
                          : selectedKycData.gender,
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField("Country", selectedKycData.country_name)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField("State", selectedKycData.state_name)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    {renderField("City", selectedKycData.city_name)}
                  </Grid>

                  <Grid item xs={12}>
                    {renderField("Address", selectedKycData.address)}
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* NIN / BVN */}
              <Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Identity Verification
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Information returned from the NIN and BVN verification
                      records.
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      showSensitive ? <VisibilityOff /> : <Visibility />
                    }
                    onClick={() => setShowSensitive((current) => !current)}
                  >
                    {showSensitive
                      ? "Hide sensitive numbers"
                      : "Show sensitive numbers"}
                  </Button>
                </Stack>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {renderIdentityCard("nin")}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {renderIdentityCard("bvn")}
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Submitted Images */}
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  Submitted Identity Images
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                      ID Card — Front
                    </Typography>

                    {renderImage(selectedKycData.id_card_f, "ID card front")}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                      ID Card — Back
                    </Typography>

                    {renderImage(selectedKycData.id_card_b, "ID card back")}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                      Applicant Photo
                    </Typography>

                    {renderImage(selectedKycData.image, "Applicant")}
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Bank Accounts / Statements */}
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  Bank Accounts
                </Typography>
                {selectedKycData.mp_banks &&
                selectedKycData.mp_banks.length > 0 ? (
                  <Grid container spacing={3}>
                    {selectedKycData.mp_banks.map((bank, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1.5}>
                            {renderField("Bank Name", bank.bank_name)}
                            {renderField("Account Number", bank.a_number, true)}
                            {renderField("Account Name", bank.a_name)}
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No bank account details provided.
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Utility Bills */}
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  Utility Bills
                </Typography>
                {selectedKycData.mp_utility_bills &&
                selectedKycData.mp_utility_bills.length > 0 ? (
                  <Grid container spacing={3}>
                    {selectedKycData.mp_utility_bills.map((bill, index) => {
                      const fileUrl = bill.file;
                      const dataUrl = bill.data;
                      const isImage =
                        fileUrl &&
                        /\.(jpeg|jpg|png|gif|webp)$/i.test(
                          fileUrl.split("?")[0],
                        );

                      return (
                        <Grid item xs={12} md={6} key={index}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                              <Typography fontWeight={700} variant="subtitle2">
                                Utility Bill #{index + 1}
                              </Typography>

                              {isImage ? (
                                renderImage(
                                  fileUrl,
                                  `Utility bill ${index + 1}`,
                                  220,
                                )
                              ) : (
                                <Box
                                  sx={{
                                    height: 220,
                                    border: "1px solid #ddd",
                                    borderRadius: 2,
                                    backgroundColor: "#f8f8f8",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  <InsertDriveFile
                                    sx={{
                                      fontSize: 48,
                                      color: "text.secondary",
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Open File
                                  </Button>
                                </Box>
                              )}

                              {dataUrl && (
                                <Box>
                                  <Button
                                    variant="text"
                                    size="small"
                                    href={dataUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View Extracted Data JSON
                                  </Button>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No utility bills provided.
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Application details */}
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  Application Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    {renderField("Marketplace ID", selectedKyc.id)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    {renderField("Profile ID", selectedKyc.profile)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    {renderField("KYC Record ID", selectedKycData.id)}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    {renderField(
                      "Application Date",
                      formatDate(selectedKyc.date),
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    {renderField(
                      "KYC Submitted",
                      formatDate(selectedKycData.date),
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    {renderField("Status", selectedKyc.kyc)}
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeKyc} sx={{ color: "black" }}>
            Close
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutline />}
            onClick={handleApprove}
            disabled={
              approving ||
              loading ||
              !selectedKyc ||
              selectedKyc.kyc !== "pending"
            }
          >
            {approving ? "Approving..." : "Approve Account"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </div>
  );
};

export default KYCApproval;
